import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { Star, Sparkles } from 'lucide-react';

interface LootBoxProps {
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
  isOpen?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isBeeping?: boolean;
  showLightPillar?: boolean;
  highestRarity?: 'common' | 'rare' | 'epic' | 'legendary' | 'jackpot';
}

const RARITY_PILLAR = {
  common: {
    bg: 'from-white via-white/80 to-transparent',
    shadow: 'shadow-[0_0_25px_rgba(255,255,255,0.4)]'
  },
  rare: {
    bg: 'from-[#38bdf8] via-[#38bdf8]/60 to-transparent',
    shadow: 'shadow-[0_0_35px_rgba(56,189,248,0.6)]'
  },
  epic: {
    bg: 'from-[#a855f7] via-[#a855f7]/60 to-transparent',
    shadow: 'shadow-[0_0_40px_rgba(168,85,247,0.7)]'
  },
  legendary: {
    bg: 'from-[#fbbf24] via-[#fbbf24]/60 to-transparent',
    shadow: 'shadow-[0_0_45px_rgba(251,191,36,0.8)]'
  },
  jackpot: {
    bg: 'from-[#ec4899] via-[#8b5cf6]/60 to-transparent',
    shadow: 'shadow-[0_0_55px_rgba(236,72,153,0.8)]'
  }
};

const TIER_COLORS = {
  bronze: {
    base: 'bg-[#a3684d]',
    accent: 'bg-[#854d35]',
    trim: 'border-[#6b3a1f]',
    glow: 'shadow-[0_0_20px_rgba(163,104,77,0.3)]',
    metal: 'bg-orange-200/50'
  },
  silver: {
    base: 'bg-[#cbd5e1]',
    accent: 'bg-[#94a3b8]',
    trim: 'border-[#64748b]',
    glow: 'shadow-[0_0_25px_rgba(203,213,225,0.4)]',
    metal: 'bg-blue-100/60'
  },
  gold: {
    base: 'bg-[#fbbf24]',
    accent: 'bg-[#d97706]',
    trim: 'border-[#92400e]',
    glow: 'shadow-[0_0_35px_rgba(251,191,36,0.6)]',
    metal: 'bg-yellow-100/70'
  },
  diamond: {
    base: 'bg-[#0ea5e9]',
    accent: 'bg-[#0369a1]',
    trim: 'border-[#075985]',
    glow: 'shadow-[0_0_45px_rgba(14,165,233,0.7)]',
    metal: 'bg-cyan-100/80'
  }
};

export default function LootBox({ tier, isOpen = false, className, size = 'md', isBeeping = false, showLightPillar = false, highestRarity }: LootBoxProps) {
  const colors = TIER_COLORS[tier];
  const sizeClass = size === 'xl' ? 'w-48 h-40' : 
                    size === 'lg' ? 'w-24 h-20' : 
                    size === 'md' ? 'w-16 h-14' : 
                    size === 'sm' ? 'w-8 h-7' : 
                    'w-6 h-5'; // xs
  
  const roundClass = size === 'xl' ? 'rounded-[48px]' : 
                    size === 'lg' ? 'rounded-[24px]' : 
                    size === 'md' ? 'rounded-[16px]' : 
                    size === 'sm' ? 'rounded-[8px]' : 
                    'rounded-[4px]'; // xs
  
  const bodyRoundClass = size === 'xl' ? 'rounded-b-[48px]' : 
                         size === 'lg' ? 'rounded-b-[24px]' : 
                         size === 'md' ? 'rounded-b-[16px]' : 
                         size === 'sm' ? 'rounded-b-[8px]' : 
                         'rounded-b-[4px]'; // xs

  const lidRoundClass = size === 'xl' ? 'rounded-t-[48px]' : 
                        size === 'lg' ? 'rounded-t-[24px]' : 
                        size === 'md' ? 'rounded-t-[16px]' : 
                        size === 'sm' ? 'rounded-t-[8px]' : 
                        'rounded-t-[4px]'; // xs

  const iconSizeClass = size === 'xl' ? 'w-16 h-16' :
                        size === 'lg' ? 'w-8 h-8' :
                        size === 'md' ? 'w-6 h-6' :
                        size === 'sm' ? 'w-4 h-4' :
                        'w-3 h-3'; // xs

  return (
    <div className={cn("relative group perspective-1000", sizeClass, className)}>
      {/* Box Body */}
      <div className={cn(
        "absolute inset-0 top-[30%] border-t-2 clay-base border-none overflow-hidden",
        bodyRoundClass,
        colors.base,
        colors.glow
      )}>
        {/* Decorative Front Plate - Proportional Inset */}
        <div className={cn(
          "absolute rounded-lg opacity-40", 
          colors.accent,
          size === 'xl' ? 'inset-x-6 top-6 bottom-6' :
          size === 'lg' ? 'inset-x-3 top-3 bottom-3' :
          size === 'md' ? 'inset-x-2 top-2 bottom-2' :
          size === 'sm' ? 'inset-x-1 top-1 bottom-1' :
          'inset-x-0.5 top-0.5 bottom-0.5' // xs
        )} />
        
        {/* Core Icon / Lock - Proportional Size */}
        <div className={cn(
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md flex items-center justify-center transition-all duration-200", 
          colors.accent,
          isBeeping && "scale-110 drop-shadow-[0_0_15px_rgba(249,115,22,0.8)]",
          iconSizeClass
        )}>
          {/*
            Carved-in numQ logo — single unified path with fillRule="evenodd".
            The circle ring (outer - inner hole) and the tail rect are all one
            shape so there is no see-through overlap at their intersection.
          */}
          <svg
            viewBox="0 0 100 100"
            className={cn(
              "w-[70%] h-[70%] transition-colors duration-200",
              isBeeping ? "drop-shadow-[0_0_8px_#f97316]" : "drop-shadow-[0_1px_0px_rgba(255,255,255,0.4)]"
            )}
          >
            <path
              fill="currentColor"
              fillRule="evenodd"
              className={isBeeping ? "text-orange-400" : "text-black/30"}
              d="
                M45,0
                C20.147,0 0,20.147 0,45
                C0,69.853 20.147,90 45,90
                C69.853,90 90,69.853 90,45
                C90,20.147 69.853,0 45,0 Z
                M35,45
                C35,39.477 39.477,35 45,35
                C50.523,35 55,39.477 55,45
                C55,50.523 50.523,55 45,55
                C39.477,55 35,50.523 35,45 Z
                M57,57
                L93,57
                C95.761,57 98,59.239 98,62
                L98,93
                C98,95.761 95.761,98 93,98
                L57,98
                C54.239,98 52,95.761 52,93
                L52,62
                C52,59.239 54.239,57 57,57 Z
              "
            />
          </svg>
        </div>
        {/* Bottom Shadow */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-black/10" />
      </div>

      {/* Box Lid */}
      <motion.div 
        className="absolute inset-x-0 top-0 h-[40%] z-[15] origin-bottom"
        initial={false}
        animate={{ 
            rotateX: isOpen ? -110 : 0,
            y: isOpen ? -2 : 0
        }}
        transition={{ 
            type: 'spring', 
            damping: 15, 
            stiffness: 120,
            mass: 0.8
        }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div className={cn(
          "w-full h-full border-b-4 relative overflow-hidden",
          lidRoundClass,
          colors.base,
          colors.trim
        )}>
            {/* Lid Underside Shadow (Visible when open) */}
            {isOpen && (
              <div className="absolute inset-0 bg-black/40 z-0" />
            )}
            
            {/* Top Surface Light */}
            <div className="absolute inset-x-2 top-1 h-3 bg-white/30 rounded-full blur-[1px] z-10" />
            
            {/* Metal Trim */}
            <div className={cn("absolute inset-x-0 bottom-0 h-2", colors.metal)} />
            
            {/* Corner Bolts */}
            <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 bg-white/20 rounded-full z-10" />
            <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-white/20 rounded-full z-10" />
        </div>
      </motion.div>

      {/* Shine Effect */}
      {!isOpen && (
        <motion.div 
           animate={{ x: ['-100%', '200%'] }}
           transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
           className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg] pointer-events-none z-20"
        />
      )}

      {/* Open Light Pillar Emergence */}
      <AnimatePresence>
        {showLightPillar && (() => {
          const pillar = highestRarity ? RARITY_PILLAR[highestRarity] : null;
          const bgClass = pillar ? pillar.bg : "from-white via-white/80 to-transparent";
          const shadowClass = pillar ? pillar.shadow : colors.glow;
          return (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: '500px', opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className={cn("absolute bottom-[30%] left-[10%] right-[10%] bg-gradient-to-t blur-xl -z-10 origin-bottom", bgClass, shadowClass)}
            />
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
